import React, { useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { InputLabel, FormHelperText, Tooltip, IconButton, Button, Box, Grid, ButtonGroup } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import SignaturePad from 'react-signature-canvas';
import { InputText, SelectBox, DateTimePicker, PassButton, FailButton, NAButton, PrimaryButton, SecondaryButton } from '../../../../../Core/Controls';
import DialogComp from '../../../../../Core/Dialog/DialogComp';
import VisibilityIcon from '@material-ui/icons/Visibility';
import BorderColorIcon from '@material-ui/icons/BorderColor';
import { useWindowSize } from '../../../../../Core/Hooks/useWindowSize';

const useStyles = makeStyles((theme) => ({
    fieldContainer: {
        marginBottom: '2px',
        width: '100%',
        transition: 'all 0.3s ease',
        display: 'flex'
        // justifyContent: 'space-between'
        // '&:hover': {
        //     transform: 'translateY(-1px)'
        // }
    },
    label: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#495057',
        marginBottom: '8px',
        display: 'inline-block'
    },
    errorText: {
        color: '#f50057',
        fontSize: '12px',
        marginTop: '4px',
        fontWeight: 500
    },
    buttonGroup: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        width: '100%'
        // marginTop: '4px'
    },
    customButton: {
        textTransform: 'none',
        borderRadius: '6px',
        fontWeight: 600,
        padding: '6px 16px',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid rgba(0, 0, 0, 0.12)'
    },
    customButtonSelected: {
        backgroundColor: '#f50057',
        color: '#fff',
        boxShadow: '0px 4px 10px rgba(245, 0, 87, 0.3)',
        border: '1px solid #f50057',
        '&:hover': {
            backgroundColor: '#c51162',
            border: '1px solid #c51162'
        }
    }
}));

export const CheckFieldType = ({
    fieldTypeId,
    name,
    value,
    onChange,
    label,
    placeholder,
    options = [],
    error,
    disabled = false,
    required = false,
    btnList,
    CheckButtons,
    changeState,
    p,
    listType,
    listOptionsVM = [],
    photoUploadComponent
}) => {
    const classes = useStyles();
    const sigCanvas = useRef({});
    const [showSign, setShowSign] = useState(false);

    const [widths, height] = useWindowSize();

    const handleSignatureEnd = () => {
        if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
            const dataUrl = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
            onChange({ target: { name, value: dataUrl } });
        }
    };

    const handleClearSignature = () => {
        if (sigCanvas.current && sigCanvas.current.clear) {
            sigCanvas.current.clear();
        }
        onChange({ target: { name, value: '' } });
    };

    // Helper to simulate a native event object for custom component interactions
    const triggerChange = (val) => {
        onChange({ target: { name, value: val } });
    };

    const renderField = () => {
        switch (fieldTypeId) {
            case 'CO':
                return (
                    <div className={classes.buttonGroup} style={{ marginBottom: 5 }}>
                        <Grid container spacing={1} alignItems="center" style={{ gap: widths > 600 ? 5 : widths < 430 ? (widths < 390 ? 10 : 16) : 27 }}>
                            {btnList.map((b) => {
                                const Comp = CheckButtons[b.inspectionSheetCheckOptionCode] || Button;
                                return (
                                    <Grid item key={b.inspectionSheetCheckOptionCode}>
                                        <Comp
                                            onClick={() => !disabled && changeState(b.inspectionSheetCheckOptionCode, p.inspectionSheetCheckID)}
                                            state={p.inspectionSheetCheckResultOptionCode === b.inspectionSheetCheckOptionCode}
                                            style={disabled ? { pointerEvents: 'none' } : {}}
                                        />
                                    </Grid>
                                );
                            })}
                            {/* {photoUploadComponent && <Grid item>{photoUploadComponent}</Grid>} */}
                        </Grid>
                    </div>
                );

            case 'DT': // Date Time
                return (
                    <DateTimePicker
                        placeholder={placeholder || label}
                        name={name}
                        value={value || ''}
                        onChange={onChange}
                        disabled={disabled}
                        style={{ backgroundColor: '#fff', width: '100%' }}
                    />
                );

            case 'LT': // List
                const selectedList = listOptionsVM.filter((k) => k.inspectionSheetListId === p.inspectionSheetCheckListTypeID) || [];
                return (
                    <Box display="flex" justifyContent="flex-start">
                        <ButtonGroup variant="outlined" color="primary" aria-label="inspection options" size="small">
                            {selectedList.map((k) => {
                                const isSelected = value === k.name;

                                return (
                                    <Button
                                        key={k.id || k.name}
                                        variant={isSelected ? 'contained' : 'outlined'}
                                        disabled={disabled}
                                        onClick={() => triggerChange(k.name)}
                                        style={
                                            isSelected
                                                ? {
                                                      backgroundColor: k.backgroundColour,
                                                      color: k.foregroundColour,
                                                      borderColor: k.backgroundColour,
                                                      fontSize: 10
                                                  }
                                                : { fontSize: 10 }
                                        }
                                    >
                                        {k.name}
                                    </Button>
                                );
                            })}
                        </ButtonGroup>
                    </Box>
                );

            case 'ML': // Multiline Text
                return (
                    <InputText
                        multiline
                        rows={4}
                        placeholder={placeholder || label}
                        name={name}
                        value={value || ''}
                        onChange={onChange}
                        disabled={disabled}
                        fullWidth
                        style={{ fontSize: 12 }}
                    />
                );

            case 'SB': // Signature
                return (
                    <div style={{ position: 'relative' }}>
                        <Button
                            color="primary"
                            variant={value ? 'contained' : 'outlined'}
                            onClick={() => setShowSign(true)}
                            startIcon={disabled ? <VisibilityIcon /> : <BorderColorIcon />}
                        >
                            Signature
                        </Button>
                        {showSign ? (
                            <DialogComp onClose={() => setShowSign(false)} title="Signature">
                                <div style={{ padding: 10 }}>
                                    {value ? (
                                        <Box
                                            style={{
                                                position: 'relative',
                                                border: '1px solid rgba(0, 0, 0, 0.23)',
                                                borderRadius: 6,
                                                padding: '12px',
                                                background: '#fcfcfc',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                minWidth: '350px',
                                                minHeight: 150,
                                                boxSizing: 'border-box'
                                            }}
                                        >
                                            <img
                                                src={value}
                                                alt="signature"
                                                style={{
                                                    display: 'block',
                                                    maxHeight: 120,
                                                    width: '100%',
                                                    objectFit: 'contain'
                                                }}
                                            />
                                            {!disabled && (
                                                <Tooltip title="Clear and Resign">
                                                    <IconButton
                                                        onClick={handleClearSignature}
                                                        style={{
                                                            position: 'absolute',
                                                            top: 6,
                                                            right: 6,
                                                            color: '#f50057',
                                                            backgroundColor: 'rgba(245, 0, 87, 0.05)'
                                                        }}
                                                        size="small"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    ) : (
                                        <Box
                                            style={{
                                                border: '1px solid rgba(0, 0, 0, 0.23)',
                                                borderRadius: 6,
                                                background: '#fff',
                                                position: 'relative',
                                                minWidth: '350px',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            <SignaturePad
                                                ref={sigCanvas}
                                                canvasProps={{
                                                    style: {
                                                        width: '100%',
                                                        height: 150,
                                                        display: 'block',
                                                        cursor: 'crosshair',
                                                        backgroundColor: '#fafafa'
                                                    },
                                                    className: 'sigPad'
                                                }}
                                                onEnd={handleSignatureEnd}
                                            />
                                            <Tooltip title="Clear">
                                                <IconButton
                                                    onClick={handleClearSignature}
                                                    style={{
                                                        position: 'absolute',
                                                        top: 6,
                                                        right: 6,
                                                        color: '#f50057',
                                                        backgroundColor: 'rgba(255, 255, 255, 0.8)'
                                                    }}
                                                    size="small"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    )}
                                </div>
                            </DialogComp>
                        ) : null}
                    </div>
                );

            case 'SL': // Single Line Text
                return (
                    <InputText
                        placeholder={placeholder || label}
                        name={name}
                        value={value || ''}
                        onChange={onChange}
                        disabled={disabled}
                        fullWidth
                        style={{ backgroundColor: '#fff' }}
                    />
                );

            default:
                return null;
        }
    };

    const renderFieldWithPhoto = () => {
        const field = renderField();
        if (photoUploadComponent) {
            return (
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', flex: 1, width: '100%' }}>
                    <div style={{ flex: 1 }}>{field}</div>
                    <div>{photoUploadComponent}</div>
                </div>
            );
        }
        return field;
    };

    return (
        <div className={classes.fieldContainer}>
            {label && (
                <InputLabel required={required} error={!!error} className={classes.label} shrink>
                    {label}
                </InputLabel>
            )}
            {renderFieldWithPhoto()}
            {error && (
                <FormHelperText error className={classes.errorText}>
                    {error}
                </FormHelperText>
            )}
        </div>
    );
};
